import { html, LitElement, nothing } from 'lit';
import { styles } from '../../../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { GridType, RuleType, Severity } from '../../../../customRulesGrid/types';
import { customElement, property } from 'lit/decorators.js';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';

@customElement('gz-rule-outcome')
export class GzRuleOutcomeWebComponent extends LitElement {
  @property({ type: String, attribute: false, reflect: false })
  gridType: GridType;

  @property({ type: Boolean })
  isGZ = false;

  @property({ type: Object, attribute: false, reflect: false })
  rule: any = null;

  @property({ type: Boolean, attribute: false, reflect: false })
  addTagOpen = false;

  @property({ type: Boolean, attribute: false, reflect: false })
  public isEdrStandalone: boolean;

  @property({ type: Boolean, attribute: false, reflect: false })
  public areAutomaticActionsAvailable: boolean;

  static get styles() {
    return styles;
  }

  render() {
    if (this.gridType === GridType.DETECTION) {
      return this.renderDetection();
    } else {
      return this.renderExclusion();
    }
  }

  private renderDetection() {
    const severityOptions = [
      {
        title: translateFn('Low'),
        value: Severity.LOW,
      },
      {
        title: translateFn('Medium'),
        value: Severity.MEDIUM,
      },
      {
        title: translateFn('High'),
        value: Severity.HIGH,
      },
    ];

    let infoSeverity, infoText;

    if (this.rule.generateRca) {
      infoSeverity = InfoBoxSeverity.INFORMATIVE;
      infoText = this.isGZ
        ? translateFn('The generated alerts will be displayed in the Incidents page as part of an incident.')
        : translateFn('The generated alerts will be displayed in the Incidents page as part of an incident. You can also browse all the alerts in the Search page.');
    } else if (!this.isEdrStandalone && this.areAutomaticActionsAvailable) {
      infoSeverity = InfoBoxSeverity.WARNING;
      infoText = translateFn('Automatic response actions are unavailable when the Generate security incident option is disabled.');
    }

    return html`
      <div class="d-flex flex-column mt-4 mb-3">
        <gz-text class="section-title mb-2" fontSize="18" fontweight="500">${translateFn('Rule outcome')}</gz-text>

        <div class="d-flex rule-outcome-container flex-row mb-2">
          <gz-tooltip
            class="rule-outcome-label"
            tooltipText="${translateFn('Generate alert with severity level')}"
            isEllipsis
          >
            <gz-text fontSize="13">${translateFn('Generate alert with severity level')}</gz-text>
          </gz-tooltip>
          <div class="d-flex flex-column rule-outcome-content">
            <gz-dropdown
              class="mb-1"
              .value="${this.rule.severity}"
              .options="${severityOptions}"
              .placeholder="${translateFn('Select severity')}"
              ?disabled="${this.addTagOpen}"
              id="severity"
              @selectionChanged="${this.handleSeverityChange}"
            ></gz-dropdown>
            ${this.renderGenerateIncidentCheckbox()}
          </div>
        </div>
        ${infoSeverity && infoText ? html`<gz-info-box class="info-box" severity="${infoSeverity}">${infoText}</gz-info-box>` : nothing}
      </div>
    `;
  }

  renderGenerateIncidentCheckbox() {
    if (this.rule.type === RuleType.YARA || this.isGZ) {
      return html`
        <gz-checkbox
          id="generate-incident"
          .value="${this.rule.generateRca}"
          .label=${translateFn('Generate security incident')}
          checked
          readonly
        >
        </gz-checkbox>
      `;
    }

    return html`
      <gz-checkbox
        id="generate-incident"
        @change="${this.handleGenerateIncidentChange}"
        .label=${translateFn('Generate security incident')}
        .checked="${this.rule.generateRca}"
        ?disabled="${this.addTagOpen}"
      >
      </gz-checkbox>
    `;
  }

  private handleSeverityChange(e: CustomEvent) {
    this.rule.severity = e.detail.value;
  }

  private renderExclusion() {
    const outcomeText = this.isGZ
      ? translateFn('Stop generating incidents.')
      : translateFn('Save all events, but stop generating incidents.')

    const infoText = this.isGZ
      ? translateFn(
          'This behavior will no longer be treated by EDR as a suspicious/malicious detection. In case this alert triggers future incidents, they will no longer appear in the Incidents page.'
        )
      : translateFn(
          'This behavior will no longer be treated as suspicious. If this alert triggers future incidents, they will no longer appear in the Incidents page. You can still view the events in the Search page.'
        );
    return html`
      <div class="d-flex flex-column mt-4 mb-3">
        <gz-text class="section-title mb-2" fontSize="18" fontweight="500">${translateFn('Rule outcome')}</gz-text>

        <gz-text fontSize="12">${outcomeText}</gz-text>

        <gz-info-box class="info-box pt-3" .severity="${'informative'}">${infoText} </gz-info-box>
      </div>
    `;
  }

  private handleGenerateIncidentChange(event) {
    this.rule.generateRca = event.detail.checked;

    this.requestUpdate('rule');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rule-outcome': GzRuleOutcomeWebComponent;
  }
}
